<script setup>
// Agenda de um dia da conta, na lateral da conversa, para conferir horários livres
// enquanto conversa com o cliente. Só leitura: usa a API da Agenda com skip_sync
// (nunca importa do Google daqui) e as entregas/retiradas de contratos do dia.
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DateField from 'dashboard/components-next/date-field/DateField.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TalkviaCalendarEventsAPI from 'dashboard/api/talkviaCalendarEvents';
import { ContractsAPI } from 'dashboard/api/talkviaContracts';
import {
  calendarAt,
  calendarDateKey,
  calendarFormat,
  calendarInstant,
  calendarTimeValue,
  shiftCalendarDays,
} from '../calendar/calendarTime';
import {
  calendarSourceLabel,
  contractDayItems,
} from '../calendar/contractCalendarEvents';
import {
  dayQuantityTotals,
  formatDayTotals,
  isDayNote,
} from '../calendar/dayQuantities';

const emit = defineEmits(['summary']);

const copy = {
  previousDay: 'Dia anterior',
  nextDay: 'Próximo dia',
  today: 'Hoje',
  dateLabel: 'Data da agenda',
  loading: 'Carregando agenda…',
  empty: 'Nenhum compromisso neste dia.',
  loadFailed: 'Não foi possível carregar a agenda.',
  retry: 'Tentar novamente',
  allDay: 'Dia inteiro',
  continues: 'Contínuo',
  canceled: 'Cancelado',
  openAgenda: 'Abrir na Agenda',
  untitled: 'Sem título',
};

const route = useRoute();
const router = useRouter();

const todayKey = () => calendarDateKey(new Date());
const selectedDay = ref(todayKey());
const items = ref([]);
const isLoading = ref(false);
const hasError = ref(false);
let request = 0;
let alerted = false;

const isToday = computed(() => selectedDay.value === todayKey());
const dayLabel = computed(() =>
  calendarFormat(calendarAt(selectedDay.value, '12:00:00.000'), {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  })
);

const sortedItems = computed(() =>
  [...items.value].sort((a, b) => {
    const noteOrder = Number(isDayNote(b)) - Number(isDayNote(a));
    if (noteOrder) return noteOrder;
    return calendarInstant(a.starts_at) - calendarInstant(b.starts_at);
  })
);

const totalsLabel = computed(() =>
  formatDayTotals(dayQuantityTotals(items.value))
);

watch(totalsLabel, value => emit('summary', value), { immediate: true });
onBeforeUnmount(() => emit('summary', ''));

const loadDay = async () => {
  request += 1;
  const current = request;
  const day = selectedDay.value;
  const params = {
    start_at: calendarAt(day).toISOString(),
    end_at: calendarAt(day, '23:59:59.999').toISOString(),
  };
  isLoading.value = true;
  hasError.value = false;
  items.value = [];

  try {
    const [eventsResponse, contractsResponse] = await Promise.all([
      TalkviaCalendarEventsAPI.get({ ...params, skip_sync: true }),
      // Contratos complementam a agenda; sem acesso a eles, seguem só os eventos.
      ContractsAPI.get({ has_scheduled_at: true, ...params }).catch(() => ({
        data: { contracts: [] },
      })),
    ]);
    if (current !== request) return;
    const events = Array.isArray(eventsResponse?.data)
      ? eventsResponse.data
      : [];
    items.value = [
      ...events,
      ...contractDayItems(contractsResponse?.data?.contracts, day),
    ];
    alerted = false;
  } catch {
    if (current !== request) return;
    hasError.value = true;
    // Um aviso por sequência de falhas; o botão de nova tentativa fica na tela.
    if (!alerted) useAlert(copy.loadFailed);
    alerted = true;
  } finally {
    if (current === request) isLoading.value = false;
  }
};

watch(selectedDay, loadDay, { immediate: true });

const onPickDate = value => {
  selectedDay.value = value || todayKey();
};
const shiftDay = amount => {
  selectedDay.value = calendarDateKey(
    shiftCalendarDays(calendarAt(selectedDay.value, '12:00:00.000'), amount)
  );
};
const goToday = () => {
  selectedDay.value = todayKey();
};

const startsOnDay = event =>
  calendarDateKey(event.starts_at) === selectedDay.value;
const timeLabel = event =>
  startsOnDay(event) ? calendarTimeValue(event.starts_at) : copy.continues;
const endLabel = event => {
  if (!event.ends_at || event.source === 'contract') return '';
  const end = calendarInstant(event.ends_at);
  if (end <= calendarInstant(event.starts_at)) return '';
  return calendarDateKey(end) === selectedDay.value
    ? calendarTimeValue(end)
    : '';
};
const isCanceled = event => event.status === 'canceled';
const itemKey = event => `${event.source || 'event'}-${event.id}`;

const openInAgenda = event => {
  router.push({
    name: 'talkvia_calendar',
    params: { accountId: route.params.accountId },
    query: {
      date: selectedDay.value,
      ...(event?.id ? { event_id: String(event.id) } : {}),
    },
  });
};
</script>

<template>
  <div class="day-agenda flex min-w-0 flex-col gap-2 p-2">
    <div class="flex min-w-0 items-center gap-1">
      <Button
        icon="i-hugeicons-arrow-left-01"
        variant="ghost"
        color="slate"
        size="xs"
        type="button"
        :aria-label="copy.previousDay"
        :title="copy.previousDay"
        data-test="day-agenda-previous"
        @click="shiftDay(-1)"
      />
      <div class="min-w-0 flex-1">
        <DateField
          :model-value="selectedDay"
          size="sm"
          :clearable="false"
          :placeholder="copy.dateLabel"
          @update:model-value="onPickDate"
        />
      </div>
      <Button
        icon="i-hugeicons-arrow-right-01"
        variant="ghost"
        color="slate"
        size="xs"
        type="button"
        :aria-label="copy.nextDay"
        :title="copy.nextDay"
        data-test="day-agenda-next"
        @click="shiftDay(1)"
      />
      <Button
        :label="copy.today"
        variant="faded"
        color="slate"
        size="xs"
        type="button"
        :disabled="isToday"
        data-test="day-agenda-today"
        @click="goToday"
      />
    </div>
    <p
      class="m-0 truncate px-1 text-xs capitalize text-n-slate-11"
      data-test="day-agenda-label"
    >
      {{ dayLabel }}
    </p>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 px-1 py-2 text-xs text-n-slate-11"
      role="status"
      data-test="day-agenda-loading"
    >
      <Spinner :show-message="false" :size="14" />
      <span>{{ copy.loading }}</span>
    </div>

    <div
      v-else-if="hasError"
      class="flex flex-wrap items-center justify-between gap-2 px-1 py-2 text-xs text-n-slate-11"
      data-test="day-agenda-error"
    >
      <span class="min-w-0">{{ copy.loadFailed }}</span>
      <Button
        :label="copy.retry"
        icon="i-hugeicons-refresh"
        variant="faded"
        color="slate"
        size="xs"
        type="button"
        data-test="day-agenda-retry"
        @click="loadDay"
      />
    </div>

    <p
      v-else-if="!sortedItems.length"
      class="m-0 px-1 py-2 text-xs text-n-slate-11"
      data-test="day-agenda-empty"
    >
      {{ copy.empty }}
    </p>

    <ul v-else class="m-0 flex list-none flex-col gap-1 p-0">
      <li v-for="event in sortedItems" :key="itemKey(event)" class="min-w-0">
        <button
          v-if="isDayNote(event)"
          type="button"
          class="day-agenda__note"
          :title="`${copy.openAgenda}: ${event.title || copy.untitled}`"
          data-test="day-agenda-note"
          @click="openInAgenda(event)"
        >
          <span
            class="i-hugeicons-sticky-note-01 mt-px size-3.5 shrink-0 text-n-amber-11"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1">
            <span
              class="block text-[10px] font-semibold uppercase tracking-wide text-n-amber-11"
            >
              {{ copy.allDay }}
            </span>
            <span
              class="block break-words text-xs text-n-slate-12"
              :class="{ 'line-through text-n-slate-10': isCanceled(event) }"
            >
              {{ event.title || copy.untitled }}
            </span>
          </span>
        </button>
        <button
          v-else
          type="button"
          class="day-agenda__row"
          :title="`${copy.openAgenda}: ${event.title || copy.untitled}`"
          data-test="day-agenda-row"
          @click="openInAgenda(event)"
        >
          <span
            class="w-11 shrink-0 text-xs font-semibold tabular-nums text-n-slate-12"
            data-test="day-agenda-time"
          >
            {{ timeLabel(event) }}
            <span
              v-if="endLabel(event)"
              class="block text-[10px] font-normal text-n-slate-10"
            >
              {{ endLabel(event) }}
            </span>
          </span>
          <span class="min-w-0 flex-1">
            <span
              class="block break-words text-xs text-n-slate-12"
              :class="{ 'line-through text-n-slate-10': isCanceled(event) }"
            >
              {{ event.title || copy.untitled }}
            </span>
            <span class="mt-0.5 flex flex-wrap gap-1">
              <span
                v-if="calendarSourceLabel(event)"
                class="day-agenda__chip"
                data-test="day-agenda-source"
              >
                {{ calendarSourceLabel(event) }}
              </span>
              <span v-if="isCanceled(event)" class="day-agenda__chip">
                {{ copy.canceled }}
              </span>
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.day-agenda__row,
.day-agenda__note {
  @apply flex w-full min-w-0 items-start gap-2 rounded-lg border border-solid px-2 py-1.5 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}

.day-agenda__row {
  @apply border-n-weak bg-n-solid-1 hover:bg-n-alpha-2;
}

/* Anotação do dia: faixa âmbar suave, sem coluna de horário, para não parecer
   um compromisso marcado. */
.day-agenda__note {
  @apply border-n-amber-7 bg-n-amber-2 hover:bg-n-amber-3;
}

.day-agenda__chip {
  @apply inline-flex max-w-full truncate rounded-md bg-n-alpha-2 px-1.5 py-px text-[10px] font-medium text-n-slate-11;
}
</style>
